import { Fragment, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Table } from '@/common/components/table'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { Stack } from '@/common/components/stack'
import { colors } from '@/core/styles/tokens'
import {
  EVENT_TYPE_LABELS,
  useNotificationActivity,
  useNotificationEntries,
  useNotificationRules,
  useToggleNotificationRule,
  type EntriesParams,
  type NotificationLogGroup,
} from '@/common/hooks/useNotificationAdmin'

function formatDay(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function groupKey(g: NotificationLogGroup): string {
  return `${g.event_type}|${g.send_date}|${g.channel}`
}

function ActivitySection() {
  const { data, isLoading, error } = useNotificationActivity()
  const [expanded, setExpanded] = useState<EntriesParams | null>(null)
  const expandedKey = expanded
    ? `${expanded.event_type}|${expanded.date}|${expanded.channel}`
    : null
  const entries = useNotificationEntries(expanded)

  const toggleExpand = (g: NotificationLogGroup) => {
    if (expandedKey === groupKey(g)) {
      setExpanded(null)
    } else {
      setExpanded({
        event_type: g.event_type,
        pay_period_id: g.pay_period_id ?? data!.pay_period.id,
        date: g.send_date,
        channel: g.channel,
      })
    }
  }

  if (isLoading) return <LoadingSpinner />
  if (error) return <Text color="error">Failed to load activity: {error.message}</Text>
  if (!data) return null

  return (
    <Stack gap="8">
      <Text size="lg" weight="bold">
        Sent notifications
      </Text>
      <Text size="sm" color="muted">
        Pay period {formatDay(data.pay_period.start_date)} – {formatDay(data.pay_period.end_date)}
      </Text>

      <Table.Root size="md">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Notification</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Sent on</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Channel</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell align="right">Sent</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell align="right">Failed</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell align="right">Recipients</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.groups.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={7} align="center">
                <Text color="muted">No notifications sent for this pay period yet.</Text>
              </Table.Cell>
            </Table.Row>
          ) : (
            data.groups.map((g) => {
              const key = groupKey(g)
              const isExpanded = expandedKey === key
              return (
                <Fragment key={key}>
                  <Table.Row>
                    <Table.Cell>{EVENT_TYPE_LABELS[g.event_type]}</Table.Cell>
                    <Table.Cell>{formatDay(g.send_date)}</Table.Cell>
                    <Table.Cell>{g.channel === 'in_app' ? 'In-app' : 'Email'}</Table.Cell>
                    <Table.Cell align="right">{g.sent_count}</Table.Cell>
                    <Table.Cell align="right">
                      {g.failed_count > 0 ? (
                        <Text color="error">{g.failed_count}</Text>
                      ) : (
                        g.failed_count
                      )}
                    </Table.Cell>
                    <Table.Cell align="right">{g.recipient_count}</Table.Cell>
                    <Table.Cell align="right">
                      <button
                        onClick={() => toggleExpand(g)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: colors.sea,
                          cursor: 'pointer',
                          fontSize: 13,
                        }}
                      >
                        {isExpanded ? 'Hide' : 'Recipients'}
                      </button>
                    </Table.Cell>
                  </Table.Row>
                  {isExpanded && (
                    <Table.Row>
                      <Table.Cell colSpan={7}>
                        {entries.isLoading ? (
                          <LoadingSpinner />
                        ) : entries.error ? (
                          <Text color="error">Failed to load recipients: {entries.error.message}</Text>
                        ) : (
                          <Stack gap="4">
                            {(entries.data ?? []).map((e) => (
                              <Text key={e.id} size="sm">
                                {e.recipient_name ?? `Employee #${e.recipient_employee_id}`}
                                {e.recipient_email ? ` (${e.recipient_email})` : ''} —{' '}
                                {e.status === 'failed' ? (
                                  <Text as="span" color="error">
                                    failed{e.error_message ? `: ${e.error_message}` : ''}
                                  </Text>
                                ) : (
                                  'sent'
                                )}{' '}
                                · {formatDateTime(e.sent_at)}
                              </Text>
                            ))}
                          </Stack>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Fragment>
              )
            })
          )}
        </Table.Body>
      </Table.Root>
    </Stack>
  )
}

function ControlsSection() {
  const { data: rules, isLoading, error } = useNotificationRules()
  const toggle = useToggleNotificationRule()

  if (isLoading) return <LoadingSpinner />
  if (error) return <Text color="error">Failed to load rules: {error.message}</Text>

  return (
    <Stack gap="8">
      <Text size="lg" weight="bold">
        Notification controls
      </Text>
      <Text size="sm" color="muted">
        Turn notification types on or off. Disabled notifications are not sent.
      </Text>

      {toggle.isError && (
        <Text color="error">Failed to update: {(toggle.error as Error).message}</Text>
      )}

      <Table.Root size="md">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Notification</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell align="right">Enabled</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rules && rules.length > 0 ? (
            rules.map((rule) => {
              const pending = toggle.isPending && toggle.variables?.id === rule.id
              return (
                <Table.Row key={rule.id}>
                  <Table.Cell>{EVENT_TYPE_LABELS[rule.event_type] ?? rule.event_type}</Table.Cell>
                  <Table.Cell align="right">
                    <button
                      onClick={() => toggle.mutate({ id: rule.id, enabled: !rule.enabled })}
                      disabled={pending}
                      aria-pressed={rule.enabled}
                      style={{
                        minWidth: 56,
                        padding: '4px 10px',
                        borderRadius: 6,
                        border: `1px solid ${rule.enabled ? colors.success : colors.gray20}`,
                        background: rule.enabled ? colors.successBackground : colors.white,
                        color: rule.enabled ? colors.success : colors.gray70,
                        cursor: pending ? 'default' : 'pointer',
                        fontSize: 13,
                        fontWeight: 500,
                        opacity: pending ? 0.6 : 1,
                      }}
                    >
                      {rule.enabled ? 'On' : 'Off'}
                    </button>
                  </Table.Cell>
                </Table.Row>
              )
            })
          ) : (
            <Table.Row>
              <Table.Cell colSpan={2} align="center">
                <Text color="muted">No notification rules configured.</Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </Stack>
  )
}

function AdminNotifications() {
  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
      <Stack gap="32">
        <Text size="xl" weight="bold">
          Notifications
        </Text>
        <ActivitySection />
        <ControlsSection />
      </Stack>
    </div>
  )
}

export const Route = createFileRoute('/org/notifications')({
  component: AdminNotifications,
})
